import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicLayout } from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, ShieldCheck, Loader2, MessageCircle, CheckCircle2 } from "lucide-react";

export default function Checkout() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [method, setMethod] = useState("whatsapp");
  const [note, setNote] = useState("");
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);

  const { data: pkg, isLoading } = useQuery({ queryKey: ["package", packageId], queryFn: async () => (await api.get(`/packages/${packageId}`)).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  const placeOrder = async () => {
    if (!user) { navigate("/login", { state: { from: `/checkout/${packageId}` } }); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", { package_id: packageId, payment_method: method, payment_note: note });
      setDone(data);
      toast.success("Order placed successfully!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setPlacing(false); }
  };

  const waNumber = (settings?.whatsapp_number || "").replace(/[^0-9]/g, "");

  if (isLoading) return <PublicLayout><div className="text-center text-slate-400 py-32">Loading…</div></PublicLayout>;
  if (!pkg) return <PublicLayout><div className="text-center text-slate-400 py-32">Package not found.</div></PublicLayout>;

  if (done) {
    const waLink = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(`Hi Saad IPTV, I placed order for ${pkg.name} (${pkg.duration_label}) - ${money(pkg.price, pkg.currency)}. My email: ${user.email}`)}` : null;
    return (
      <PublicLayout>
        <section className="max-w-xl mx-auto px-4 pt-20 pb-24">
          <div className="glass rounded-2xl p-10 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-5" />
            <h1 className="font-heading text-2xl font-bold text-white mb-2">Order Confirmed!</h1>
            <p className="text-slate-400 mb-6">Your order for <span className="text-white font-semibold">{pkg.name} ({pkg.duration_label})</span> is pending approval. Our team will activate it shortly after payment confirmation.</p>
            <div className="glass rounded-xl p-4 text-left text-sm space-y-2 mb-6">
              <div className="flex justify-between"><span className="text-slate-400">Order ID</span><span className="text-white font-mono">{done.id.slice(-8)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Amount</span><span className="text-white">{money(done.amount, done.currency)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Status</span><span className="text-yellow-400 capitalize">{done.status}</span></div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              {waLink && (
                <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="checkout-whatsapp-confirm" className="flex-1">
                  <Button className="w-full bg-[#25D366] hover:bg-[#1eb855] text-white border-0"><MessageCircle className="w-4 h-4 mr-2" /> Confirm on WhatsApp</Button>
                </a>
              )}
              <Button data-testid="checkout-goto-dashboard" onClick={() => navigate("/dashboard")} className="flex-1 bg-white/10 text-white hover:bg-white/20">Go to Dashboard</Button>
            </div>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <h1 className="font-heading text-3xl font-extrabold text-white mb-8">Secure Checkout</h1>
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">
            <div className="glass rounded-2xl p-6">
              <h2 className="font-heading font-semibold text-white mb-4">Payment Method</h2>
              <RadioGroup value={method} onValueChange={setMethod} className="space-y-3">
                <label className="flex items-start gap-3 glass rounded-xl p-4 cursor-pointer border-white/10 hover:border-purple-500/40 transition-colors">
                  <RadioGroupItem value="whatsapp" id="m-wa" data-testid="pay-method-whatsapp" className="mt-1" />
                  <div>
                    <div className="text-white font-medium flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp Order</div>
                    <p className="text-sm text-slate-400 mt-1">Place your order and confirm payment with our team on WhatsApp.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 glass rounded-xl p-4 cursor-pointer border-white/10 hover:border-purple-500/40 transition-colors">
                  <RadioGroupItem value="bank" id="m-bank" data-testid="pay-method-bank" className="mt-1" />
                  <div>
                    <div className="text-white font-medium">Bank Transfer / Manual</div>
                    <p className="text-sm text-slate-400 mt-1">Pay via bank transfer and send proof. Admin approves manually.</p>
                  </div>
                </label>
              </RadioGroup>
              {settings?.payment_instructions && (
                <div className="mt-4 text-xs text-slate-400 bg-white/5 border border-white/10 rounded-lg p-3">{settings.payment_instructions}</div>
              )}
              <div className="mt-5">
                <Label className="text-slate-300">Note (optional)</Label>
                <Textarea data-testid="checkout-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any details for our team…" className="mt-1.5 bg-white/5 border-white/10 text-white" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="glass rounded-2xl p-6 sticky top-24">
              <h2 className="font-heading font-semibold text-white mb-4">Order Summary</h2>
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-slate-300">{pkg.name}</span>
                <span className="text-white font-semibold">{money(pkg.price, pkg.currency)}</span>
              </div>
              <p className="text-xs text-purple-400 uppercase tracking-wide mb-4">{pkg.duration_label} · {pkg.duration_days} days</p>
              <ul className="space-y-2 mb-5">
                {pkg.features?.slice(0, 5).map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-400"><Check className="w-3.5 h-3.5 text-neon-purple" /> {f}</li>
                ))}
              </ul>
              <div className="border-t border-white/10 pt-4 flex justify-between items-baseline mb-6">
                <span className="text-slate-300 font-medium">Total</span>
                <span className="font-heading text-2xl font-extrabold text-white">{money(pkg.price, pkg.currency)}</span>
              </div>
              <Button data-testid="place-order-btn" onClick={placeOrder} disabled={placing} className="w-full glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0 py-6">
                {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : (user ? "Place Order" : "Login to Order")}
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500 mt-4">
                <ShieldCheck className="w-3.5 h-3.5" /> Reviewed &amp; activated by our team
              </p>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
